import { FC } from 'react';

import { useAuthentication } from '@ravago/shared/authentication';
import {
   OrderDocumentType,
   OrderLineBOL,
   OrderLineCOA,
   OrderLineDeliveryNote,
   OrderLineDetail,
   OrderLineDocumentResponse,
   OrderLineInvoice
} from '@ravago/shared/page-data/models/elements';
import { Accordion } from '@ravago/shared/radiance/components/accordion/components/Accordion/Accordion';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import notification from '@ravago/shared/radiance/utils/notification/notification';
import { useTranslations } from '@ravago/shared/translations/client';

import { saveAs } from 'file-saver';

import { CorrelationService } from '../../../../services/tracking/correlation-token.service';
import { TrackingUtils } from '../../../../utils/tracking/tracking.utils';

import { CoaDocuments } from './coa-documents';
import { DeliveryNoteDocuments } from './delivery-note-documents';
import { InvoiceDocuments } from './invoice-documents';

interface Props {
   orderLine: OrderLineDetail;
   documents: OrderLineDocumentResponse | undefined;
   isUSBusinessContext: boolean;
   showInvoiceDocuments: boolean;
   requestCoa: (orderNumber: string, producerLotNr: string) => Promise<boolean>;
   requestDeliveryNote: (orderNumber: string, transportOrderLineNr: number, bundledTransportOrderId?: number) => Promise<boolean>;
   requestBol: (orderNumber: string, transportOrderLineNr: number, bundledTransportOrderId?: number) => Promise<boolean>;
   downloadDocument: (downloadEndpoint: string, correlationId: string) => Promise<string | undefined>;
}

export const OrderLineDocuments: FC<Props> = ({
   orderLine,
   documents,
   isUSBusinessContext,
   showInvoiceDocuments,
   requestBol,
   requestCoa,
   requestDeliveryNote,
   downloadDocument
}) => {
   const translate = useTranslations();
   const { accessToken } = useAuthentication();

   const handleDownload = async (document: OrderLineInvoice | OrderLineCOA | OrderLineDeliveryNote, type: OrderDocumentType) => {
      if (!accessToken) return;
      const blob = await downloadDocument(
         `customer/orders/${orderLine.orderNumber}/documents/${document.id}`,
         CorrelationService.generateCorrelationId('dpcid')
      );
      if (blob) {
         saveAs(blob, document.extension ? `${document.name}.${document.extension}` : document.name);
         TrackingUtils.track('order_document_download', {
            documentType: type,
            orderNumber: orderLine.orderNumber,
            productId: orderLine.product.pkbId?.toString() || orderLine.product.id.toString(),
            productName: orderLine.product.description,
            orderReference: orderLine.customerOrderReference,
            productReference: orderLine.customerProductReference
         });
      } else {
         notification(translate('page-consumer.order-line-flyout.download-error'));
      }
   };

   const handleViewDocument = (document: OrderLineInvoice | OrderLineCOA | OrderLineDeliveryNote) => {
      const baseName = document.name ?? 'document';
      const fileName = document.extension ? `${baseName}.${document.extension}` : baseName;
      const url = `/api/customer/orders/${orderLine.orderNumber}/documents/${document.id}?filename=${encodeURIComponent(fileName)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
   };

   const handleRequestChange = async <T extends OrderLineCOA | OrderLineDeliveryNote | OrderLineBOL>(type: OrderDocumentType, document: T) => {
      if (!accessToken) {
         notification(translate('page-consumer.order-line-flyout.request-error'));
         return;
      }
      let result;

      switch (type) {
         case 'COA':
            result = await requestCoa(orderLine.orderNumber, (document as OrderLineCOA).producerLotNr);
            break;
         case 'Delivery Note':
            result = await requestDeliveryNote(
               orderLine.orderNumber,
               (document as OrderLineDeliveryNote).transportOrderLineNr,
               (document as OrderLineDeliveryNote).bundledTransportOrderId
            );
            break;
         case 'Bill of Lading':
            result = await requestBol(
               orderLine.orderNumber,
               (document as OrderLineBOL).transportOrderLineNr,
               (document as OrderLineBOL).bundledTransportOrderId
            );
            break;
      }
      if (result) {
         notification(translate('page-consumer.order-line-flyout.request-success'));
      } else {
         notification(translate('page-consumer.order-line-flyout.request-error'));
      }
   };

   return (
      <Box direction="column" gap="lg">
         {documents && (
            <>
               <Box justify="between">
                  <Typography variant="heading-4" component="h4" color="strong">
                     {translate('page-consumer.order-line-flyout.documents')}
                  </Typography>
               </Box>

               <Accordion
                  open
                  header={
                     <Box>
                        <Typography variant="body-highlight" component="span" color="strong">
                           {translate('shipment.documents.invoices-and-credit-notes')}
                        </Typography>
                     </Box>
                  }
                  body={
                     <InvoiceDocuments
                        documents={documents.invoices}
                        showInvoiceDocuments={showInvoiceDocuments}
                        onDownload={(document) => handleDownload(document, 'Invoice')}
                        onViewDocument={(document) => handleViewDocument(document)}
                     />
                  }
               />

               <Accordion
                  open
                  header={
                     <Box>
                        <Typography variant="body-highlight" component="span" color="strong">
                           {translate('page-consumer.order-line-flyout.certificates-of-analysis')}
                        </Typography>
                     </Box>
                  }
                  body={
                     <CoaDocuments
                        documents={documents.coa}
                        onDownload={(document) => handleDownload(document, 'COA')}
                        onViewDocument={(document) => handleViewDocument(document)}
                        onRequestCoa={(coa) => handleRequestChange('COA', coa)}
                     />
                  }
               />

               <Accordion
                  open
                  header={
                     <Box>
                        <Typography variant="body-highlight" component="span" color="strong">
                           {isUSBusinessContext
                              ? translate('page-consumer.order-line-flyout.bills-of-lading')
                              : translate('page-consumer.order-line-flyout.delivery-notes')}
                        </Typography>
                     </Box>
                  }
                  body={
                     <DeliveryNoteDocuments
                        deliveryNotes={documents.deliveryNotes}
                        billsOfLading={documents.billsOfLading}
                        onDownload={(document) => handleDownload(document, 'Delivery Note')}
                        onViewDocument={(document) => handleViewDocument(document)}
                        onRequestDeliveryNote={(deliveryNote) => handleRequestChange('Delivery Note', deliveryNote)}
                        onRequestBol={(bol) => handleRequestChange('Bill of Lading', bol)}
                     />
                  }
               />
            </>
         )}
      </Box>
   );
};
